<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol
                sm="6"
                class="text-left"
            >
                <DButton
                    v-if="premiumServicesFilterEnabled"
                    id="premium-services"
                    :label="__('contracts.contracts.premium_services')"
                    mini
                    @click="applyPremiumServicesFilter"
                />
            </DCol>
            <DCol
                sm="6"
                class="text-right"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <div class="search">
            <DRow
                align-horizontal="start"
                align-vertical="end"
                class="mb-3"
            >
                <DCol sm="3">
                    <DMultiSelect
                        id="product-groups-dropdown"
                        :options="productGroupOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.productGroups"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.contracts.product_group')"
                        @update="updateSelectedProductGroups"
                    />
                </DCol>
                <DCol sm="3">
                    <DMultiSelect
                        id="products-dropdown"
                        :options="productsOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.products"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.contracts.product')"
                        @update="updateSelectedProducts"
                    />
                </DCol>
                <DCol sm="3">
                    <DMultiSelect
                        id="status-dropdown"
                        :options="statusOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.status"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.contracts.status')"
                        @update="updateSelectedStatus"
                    />
                </DCol>
                <DCol sm="3">
                    <DMultiSelect
                        id="segments-dropdown"
                        :options="segmentOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.segments"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.contracts.segment')"
                        @update="updateSelectedSegments"
                    />
                </DCol>
            </DRow>
            <DRow class="mb-2">
                <DCol sm="3">
                    <DMultiSelect
                        id="brands-dropdown"
                        :options="brandOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.brands"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.contracts.brand')"
                        @update="updateSelectedBrands"
                    />
                </DCol>
                <DCol sm="3">
                    <DDateFormatInput
                        id="date-from"
                        v-model="filterData.dateFrom"
                        format="YYYY-MM-DD"
                        type="date"
                        :label="__('contracts.contracts.date_from')"
                        @keydown.enter.exact.prevent="changePage(1)"
                    />
                </DCol>
                <DCol sm="3">
                    <DDateFormatInput
                        id="date-to"
                        v-model="filterData.dateTo"
                        format="YYYY-MM-DD"
                        :label="__('contracts.contracts.date_to')"
                        type="date"
                        @keydown.enter.exact.prevent="changePage(1)"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="p-4 action-buttons"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="changePage(1)"
                    />
                    <DButton
                        id="filter-reset"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </div>

        <DRow>
            <DCol lg="12">
                <DTable
                    :items="contracts.items"
                    :fields="getFields"
                    :sorter="isSortable"
                    :sorter-value="sorterValue"
                    class="table-responsive"
                    @update:sorter-value="updateOrdering"
                >
                    <template #id="{item}">
                        <td v-if="userPermissions.includes('contract.view')">
                            <Link :href="contractShowRoute(item.customer_id, item.id)">
                                {{ item.id }}
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.id }}
                        </td>
                    </template>
                    <template #customer="{item}">
                        <td v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerProfileRoute(item.customer_id)">
                                {{ item.customer }}
                            </Link>
                        </td>
                    </template>
                    <template #segments="{item}">
                        <td>
                            {{ segmentsNames(item) }}
                        </td>
                    </template>
                    <template #create_date="{item}">
                        <td>
                            {{ setDateFormat(item.create_date) }}
                        </td>
                    </template>
                    <template #end_date="{item}">
                        <td v-if="item.end_date !== null">
                            {{ item.end_date }}
                        </td>
                        <td v-else />
                    </template>
                </DTable>
                <DPagination
                    v-if="contracts.last_page > 1"
                    :active-page="contracts.current_page"
                    :pages="contracts.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script>

import ListViewModal from '~@/Components/ListView/ListViewModal'

export default {
    name: 'ContractsIndex',
    components: { ListViewModal },
    props: {
        contracts: {
            type: Object,
            default: () => { return {} }
        },
        productGroups: {
            type: Array,
            default () {
                return []
            }
        },
        products: {
            type: Array,
            default () {
                return []
            }
        },
        status: {
            type: Array,
            default () {
                return []
            }
        },
        segments: {
            type: Array,
            default () {
                return []
            }
        },
        brands: {
            type: Array,
            default () {
                return []
            }
        },
        filters: {
            type: Object,
            default () {
                return {}
            }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        premiumServicesFilterEnabled: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            filterData: {
                product_groups: this.filters.product_groups,
                products: this.filters.products,
                status: this.filters.status,
                segments: this.filters.segments,
                brands: this.filters.brands,
                dateFrom: this.filters.dateFrom,
                dateTo: this.filters.dateTo,
                order_by: this.filters.order_by,
                order_by_asc: this.filters.order_by_asc ? 1 : 0
            },
            productsOptions: [],
            showListViewModal: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        productGroupOptions () {
            return this.productGroups.map((item) => {
                return { value: item.value, text: item.label, selected: this.filterData?.product_groups?.includes(String(item.value)) }
            })
        },
        statusOptions () {
            return this.status.map((item) => {
                return {
                    value: item.value,
                    text: this.__(`contracts.contracts.${item.label}`),
                    selected: this.filterData?.status?.includes(String(item.value))
                }
            })
        },
        segmentOptions () {
            return this.segments.map((item) => {
                return { value: item.value, text: item.label, selected: this.filterData?.segments?.includes(String(item.value)) }
            })
        },
        brandOptions () {
            return this.brands.map((item) => {
                return { value: item.value, text: item.label, selected: this.filterData?.brands?.includes(String(item.value)) }
            })
        },
        sorterValue () {
            return {
                column: this.filterData.order_by,
                asc: !!this.filters.order_by_asc
            }
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: this.__('settings.list_view.' + item.list + '.' + item.column),
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                    })
                }
            })

            return availableList
        }
    },
    created () {
        this.getProductsOptions()
    },
    methods: {
        customerProfileRoute (customerId) {
            return this.$route('customers.customers.show', { customer: customerId })
        },
        contractShowRoute (customerId, contractId) {
            return this.$route('customers.customers.contracts.show', { customer: customerId, contract: contractId })
        },
        segmentsNames (values) {
            const segments = []
            if (values.segments != null) {
                values.segments.forEach(segment => segments.push(segment.name))
            }
            return segments.join(', ')
        },
        updateSelectedProductGroups (values) {
            this.filterData.product_groups = values
            this.getProductsOptions()
        },
        updateSelectedProducts (values) {
            this.filterData.products = values
        },
        updateSelectedStatus (values) {
            this.filterData.status = values
        },
        updateSelectedSegments (values) {
            this.filterData.segments = values
        },
        updateSelectedBrands (values) {
            this.filterData.brands = values
        },
        getParamArray (paramName) {
            const params = new URLSearchParams(window.location.search)
            const paramArray = []
            const expression = new RegExp('^' + paramName + '[\\[]\\d*[\\]]$')
            for (const param of params.entries()) {
                if (param[0].match(expression)) {
                    paramArray.push(param[1])
                }
            }
            return paramArray
        },
        changePage (page) {
            const query = Object.fromEntries(Object.entries(this.filterData).filter(([_, v]) => v))

            if (page !== 1) {
                query._query = { page: page }
            }

            query.save_filters = true

            this.$inertia.get(this.$route('contracts.contracts', query))
        },
        resetFilters () {
            this.$inertia.get(this.$route('contracts.contracts', { save_filters: true, default_filters: true }))
        },
        getProductsOptions () {
            if (this.filterData.product_groups?.length > 0) {
                const selectedGroupList = this.filterData.product_groups.map((item) => {
                    return parseInt(item)
                })

                this.productsOptions = this.products
                    .filter((item) => selectedGroupList.includes(item.group_id))
                    .map((item) => {
                        return { value: item.value, text: item.label, selected: this.filterData?.products?.includes(String(item.value)) }
                    })

                return
            }

            this.productsOptions = this.products
                .map((item) => {
                    return { value: item.value, text: item.label, selected: this.filterData?.products?.includes(String(item.value)) }
                })
        },
        updateOrdering (event) {
            this.filterData.order_by = event.column
            this.filterData.order_by_asc = event.asc ? 1 : 0

            this.changePage(this.contracts.current_page)
        },
        applyPremiumServicesFilter () {
            const premiumServiceProductIds = []

            for (const product of this.products) {
                if (product.fulfillment_module === 'antagonist_premium_services') {
                    premiumServiceProductIds.push(String(product.value))
                }
            }
            this.filterData.products = premiumServiceProductIds

            this.changePage(1)
        }
    }
}
</script>
<style lang="scss" scoped>
.action-buttons {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
}
</style>
